import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { Skeleton } from "../components/ui/Skeleton";
import {
  contractsApi,
  ContractSignatureMethod,
  ContractSignatureMethods,
  ContractTemplateResponse,
  CreateContractTemplateRequest,
  LockedSectionsPreviewResponse,
} from "../api/contractsApi";
import { settingsApi } from "../api/settingsApi";

const NAME_MAX = 150;
const SERVICES_MAX = 4000;
const CUSTOM_TERMS_MAX = 8000;
const FEE_MIN_CENTS = 1;
const FEE_MAX_CENTS = 1_000_000;

interface DraftForm {
  name: string;
  feeDollars: string;
  paymentDayOfMonth: number;
  servicesDescriptionOverride: string;
  customTerms: string;
  allowedSignatureMethod: ContractSignatureMethod;
}

function fromTemplate(t: ContractTemplateResponse): DraftForm {
  return {
    name: t.name,
    feeDollars: (t.monthlyFeeCents / 100).toFixed(2),
    paymentDayOfMonth: t.paymentDayOfMonth,
    servicesDescriptionOverride: t.servicesDescriptionOverride ?? "",
    customTerms: t.customTerms ?? "",
    allowedSignatureMethod: t.allowedSignatureMethod,
  };
}

function emptyForm(): DraftForm {
  return {
    name: "Standard Service Agreement",
    feeDollars: "99.00",
    paymentDayOfMonth: 1,
    servicesDescriptionOverride: "",
    customTerms: "",
    allowedSignatureMethod: ContractSignatureMethods.Either,
  };
}

function dollarsToCents(input: string): number | null {
  const cleaned = input.trim().replace(/[$,]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const n = Math.round(parseFloat(cleaned) * 100);
  return Number.isFinite(n) ? n : null;
}

function methodLabel(m: ContractSignatureMethod): string {
  if (m === ContractSignatureMethods.Typed) return "Typed";
  if (m === ContractSignatureMethods.Drawn) return "Drawn";
  return "Either";
}

const ContractTemplatesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [templates, setTemplates] = useState<ContractTemplateResponse[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(() => emptyForm());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<LockedSectionsPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [signerLegalName, setSignerLegalName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  );

  const signerConfigured = signerLegalName.trim().length > 0 && signerTitle.trim().length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, defaultTpl, settings] = await Promise.all([
        contractsApi.listTemplates(),
        contractsApi.getOrCreateDefaultTemplate(),
        settingsApi.get(),
      ]);

      // Auto-create-default may have inserted a row not yet in `list`.
      const merged = list.some((t) => t.id === defaultTpl.id) ? list : [...list, defaultTpl];
      setTemplates(merged);
      setSelectedId((prev) => prev ?? defaultTpl.id);
      setSignerLegalName(settings.operatorSignerLegalName);
      setSignerTitle(settings.operatorSignerTitle);
    } catch {
      setError("Could not load contract templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Sync draft when the selected template changes (and we're not in create mode).
  useEffect(() => {
    if (creatingNew) return;
    if (selected) {
      setDraft(fromTemplate(selected));
    }
  }, [selected, creatingNew]);

  const validate = (): string | null => {
    const name = draft.name.trim();
    if (name.length < 1 || name.length > NAME_MAX) {
      return `Name must be 1–${NAME_MAX} characters.`;
    }
    const cents = dollarsToCents(draft.feeDollars);
    if (cents === null) {
      return "Monthly fee must be a dollar amount (e.g. 99.00).";
    }
    if (cents < FEE_MIN_CENTS || cents > FEE_MAX_CENTS) {
      return `Monthly fee must be between $${(FEE_MIN_CENTS / 100).toFixed(2)} and $${(FEE_MAX_CENTS / 100).toFixed(2)}.`;
    }
    if (
      !Number.isInteger(draft.paymentDayOfMonth) ||
      draft.paymentDayOfMonth < 1 ||
      draft.paymentDayOfMonth > 28
    ) {
      return "Payment day must be a whole number from 1 to 28.";
    }
    if (draft.servicesDescriptionOverride.length > SERVICES_MAX) {
      return `Services description must be ${SERVICES_MAX} characters or fewer.`;
    }
    if (draft.customTerms.length > CUSTOM_TERMS_MAX) {
      return `Custom terms must be ${CUSTOM_TERMS_MAX} characters or fewer.`;
    }
    return null;
  };

  const buildBody = (): CreateContractTemplateRequest => {
    const cents = dollarsToCents(draft.feeDollars)!;
    const services = draft.servicesDescriptionOverride.trim();
    const terms = draft.customTerms.trim();
    return {
      name: draft.name.trim(),
      monthlyFeeCents: cents,
      paymentDayOfMonth: draft.paymentDayOfMonth,
      servicesDescriptionOverride: services.length === 0 ? null : services,
      customTerms: terms.length === 0 ? null : terms,
      allowedSignatureMethod: draft.allowedSignatureMethod,
    };
  };

  const handleStartNew = () => {
    setCreatingNew(true);
    setSelectedId(null);
    setDraft(emptyForm());
    setError(null);
    setSuccess(null);
  };

  const handleSelect = (id: string) => {
    setCreatingNew(false);
    setSelectedId(id);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setError(v);
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body = buildBody();
      if (creatingNew || !selected) {
        const created = await contractsApi.createTemplate(body);
        setTemplates((prev) => [...prev, created]);
        setSelectedId(created.id);
        setCreatingNew(false);
        setSuccess("Template created.");
      } else {
        const updated = await contractsApi.updateTemplate(selected.id, body);
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        setSuccess("Template saved.");
      }
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await contractsApi.setDefaultTemplate(selected.id);
      // Refresh list — set-default toggles two rows.
      const list = await contractsApi.listTemplates();
      setTemplates(list);
      setSuccess("Default updated.");
    } catch {
      setError("Could not set as default.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selected) return;
    if (selected.isDefault) {
      setError("Set a different template as default before archiving this one.");
      return;
    }
    if (!window.confirm(`Archive "${selected.name}"? It will be hidden from the send modal.`)) {
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await contractsApi.archiveTemplate(selected.id);
      const list = await contractsApi.listTemplates();
      setTemplates(list);
      setSuccess("Template archived.");
    } catch {
      setError("Could not archive template.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetServices = () => {
    setDraft((d) => ({ ...d, servicesDescriptionOverride: "" }));
  };

  const handleOpenPreview = async () => {
    setPreviewOpen(true);
    if (preview) return;
    setPreviewLoading(true);
    try {
      const p = await contractsApi.getLockedPreview();
      setPreview(p);
    } catch {
      setError("Could not load locked-section preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">Contract templates</h1>
            <p className="mt-1 text-sm text-slate-400">
              Edit the variable parts of your service agreement. Federally-mandated CROA sections
              are locked and applied automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={handleStartNew}
            className="rounded-lg border border-blue-500/50 bg-blue-600/20 px-3 py-2 text-sm font-medium text-blue-200 hover:bg-blue-600/30"
          >
            New template
          </button>
        </div>

        {!signerConfigured && (
          <div className="rounded-xl border border-amber-500/45 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
            <p className="font-semibold text-amber-200">Operator signer not set</p>
            <p className="mt-1">
              Set your operator signer name and title in{" "}
              <Link to="/settings" className="font-medium text-amber-200 underline hover:text-amber-100">
                Settings
              </Link>{" "}
              before sending contracts.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-950/30 px-4 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-4 py-2 text-sm text-emerald-200">
            {success}
          </div>
        )}

        {loading ? (
          <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
            <Skeleton className="h-96 rounded-xl" />
            <Skeleton className="h-96 rounded-xl" />
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
            {/* Template list */}
            <aside className="rounded-xl border border-slate-800 bg-slate-900/70 p-3">
              <h2 className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Templates
              </h2>
              <ul className="space-y-1">
                {templates.length === 0 && (
                  <li className="px-2 py-2 text-sm text-slate-400">No templates yet.</li>
                )}
                {templates.map((t) => {
                  const active = !creatingNew && t.id === selectedId;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(t.id)}
                        className={`flex w-full flex-col gap-1 rounded-lg px-3 py-2 text-left text-sm transition ${
                          active
                            ? "bg-blue-600/20 text-blue-100"
                            : "text-slate-200 hover:bg-slate-800"
                        }`}
                      >
                        <span className="font-medium">{t.name}</span>
                        <span className="flex flex-wrap gap-1">
                          {t.isDefault && (
                            <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-200">
                              Default
                            </span>
                          )}
                          {t.isArchived && (
                            <span className="rounded-full bg-slate-500/20 px-2 py-0.5 text-xs font-medium text-slate-300">
                              Archived
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Editor */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
              <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-white">
                  {creatingNew ? "New template" : selected?.name ?? "Select a template"}
                </h2>
                {!creatingNew && selected && (
                  <p className="text-xs text-slate-500">
                    Updated {new Date(selected.updatedAt).toLocaleString()}
                  </p>
                )}
              </header>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Name
                  </label>
                  <input
                    type="text"
                    value={draft.name}
                    maxLength={NAME_MAX}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Monthly fee (USD)
                    </label>
                    <div className="mt-1 flex items-stretch overflow-hidden rounded-lg border border-slate-700 bg-slate-950/60 focus-within:border-blue-500">
                      <span className="flex items-center px-3 text-sm text-slate-400">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft.feeDollars}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, feeDollars: e.target.value }))
                        }
                        className="w-full bg-transparent px-1 py-2 text-sm text-slate-100 outline-none"
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">$0.01 minimum, $10,000 maximum.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Payment day of month
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={28}
                      value={draft.paymentDayOfMonth}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          paymentDayOfMonth: Math.max(
                            1,
                            Math.min(28, Number.parseInt(e.target.value, 10) || 1)
                          ),
                        }))
                      }
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      1–28 (avoids February edge cases).
                    </p>
                  </div>
                </div>

                <div>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Allowed signature method
                  </span>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {[
                      ContractSignatureMethods.Typed,
                      ContractSignatureMethods.Drawn,
                      ContractSignatureMethods.Either,
                    ].map((m) => (
                      <label
                        key={m}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                          draft.allowedSignatureMethod === m
                            ? "border-blue-500/60 bg-blue-600/15 text-blue-100"
                            : "border-slate-700 bg-slate-950/40 text-slate-300 hover:bg-slate-800"
                        }`}
                      >
                        <input
                          type="radio"
                          name="allowedSignatureMethod"
                          className="accent-blue-500"
                          checked={draft.allowedSignatureMethod === m}
                          onChange={() =>
                            setDraft((d) => ({ ...d, allowedSignatureMethod: m }))
                          }
                        />
                        {methodLabel(m)}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Services description override
                    </label>
                    {draft.servicesDescriptionOverride.length > 0 && (
                      <button
                        type="button"
                        onClick={handleResetServices}
                        className="text-xs font-medium text-blue-300 hover:text-blue-200"
                      >
                        Use default
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={5}
                    maxLength={SERVICES_MAX}
                    value={draft.servicesDescriptionOverride}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, servicesDescriptionOverride: e.target.value }))
                    }
                    placeholder="Leave blank to use the standard services description."
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {draft.servicesDescriptionOverride.length} / {SERVICES_MAX}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Custom terms
                  </label>
                  <textarea
                    rows={6}
                    maxLength={CUSTOM_TERMS_MAX}
                    value={draft.customTerms}
                    onChange={(e) => setDraft((d) => ({ ...d, customTerms: e.target.value }))}
                    placeholder="Optional. Appears as an additional terms section in the contract."
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    {draft.customTerms.length} / {CUSTOM_TERMS_MAX}
                  </p>
                </div>

                <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-4">
                  <div className="flex items-start gap-3">
                    <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-200">
                        Federally-locked sections
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        These sections are required by federal law (CROA, 15 U.S.C. § 1679c &amp;
                        § 1679e) and cannot be edited: Consumer Credit File Rights Disclosure,
                        Notice of Cancellation, Total Cost &amp; Payment Terms structure, and the
                        no-guaranteed-outcomes statement.
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleOpenPreview()}
                        className="mt-2 text-xs font-medium text-blue-300 hover:text-blue-200"
                      >
                        View locked text →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-800 pt-4">
                  {!creatingNew && selected && !selected.isDefault && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleSetDefault()}
                      className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                    >
                      Set as default
                    </button>
                  )}
                  {!creatingNew && selected && !selected.isArchived && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleArchive()}
                      className="rounded-lg border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-100 hover:bg-rose-950/60 disabled:opacity-50"
                    >
                      Archive
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSave()}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : creatingNew ? "Create template" : "Save changes"}
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-[90vh] w-full max-w-3xl min-w-0 flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-800 px-4 py-3">
              <div>
                <h2 className="text-lg font-semibold text-white">Locked sections</h2>
                {preview && (
                  <p className="text-xs text-slate-500">Version: {preview.version}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-lg border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800"
              >
                Close
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto p-4 text-sm text-slate-200">
              {previewLoading && <p className="text-slate-400">Loading…</p>}
              {preview && (
                <>
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Total Cost & Payment Terms (structure)
                    </h3>
                    <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">
                      {preview.totalCostStructure}
                    </pre>
                  </section>
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Consumer Credit File Rights Under State and Federal Law
                    </h3>
                    <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">
                      {preview.consumerRightsDisclosure}
                    </pre>
                  </section>
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Notice of Cancellation
                    </h3>
                    <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">
                      {preview.rightToCancel}
                    </pre>
                  </section>
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      No guaranteed outcomes
                    </h3>
                    <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">
                      {preview.noGuaranteedOutcomes}
                    </pre>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ContractTemplatesPage;
